import React, { useState } from 'react';
import { Card, Button, Row, Col, Typography, Input, Select, Tag, Drawer } from 'antd';
import { useHistory } from 'react-router-dom';
import { trackSearch } from '../../utils/analytics';
import { ArrowLeftOutlined, SearchOutlined, ReadOutlined, MenuOutlined } from '@ant-design/icons';
import { blogPosts, getAllCategories, searchPosts, getPostsByCategory } from '../../data/blogPosts';
import calmiImage from '../../assets/images/Calmi.png';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;
const { Option } = Select;

const Blog = () => {
  const history = useHistory();
  const [filteredPosts, setFilteredPosts] = useState(blogPosts);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  const categories = getAllCategories();

  const handleSearch = (value) => {
    setSearchQuery(value);
    if (value.trim()) {
      const results = searchPosts(value);
      setFilteredPosts(results);
      // Track search event
      trackSearch(value, results.length);
    } else if (selectedCategory === 'all') {
      setFilteredPosts(blogPosts);
    } else {
      setFilteredPosts(getPostsByCategory(selectedCategory));
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      setFilteredPosts(searchQuery ? searchPosts(searchQuery) : blogPosts);
    } else {
      const categoryPosts = getPostsByCategory(category);
      setFilteredPosts(searchQuery ? categoryPosts.filter(post => 
        searchPosts(searchQuery).includes(post)
      ) : categoryPosts);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header Navigation */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px 0',
        marginBottom: '0'
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              type="text" 
              icon={<ArrowLeftOutlined />} 
              onClick={() => history.push('/')}
              style={{ color: 'white', marginRight: '20px' }}
            >
              Back to Lumi Bears
            </Button>
            <img 
              src={calmiImage} 
              alt="Lumi"
              style={{ height: '40px', marginRight: '15px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML += '<span style="color: white; font-size: 24px; font-weight: bold;">🧸</span>';
              }}
            />
            <Title level={3} style={{ color: 'white', margin: 0 }}>
              Lumi Bears Blog
            </Title>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Button
              onClick={() => history.push('/wellness')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                height: '40px',
                fontWeight: '500'
              }}
            >
              How We Help
            </Button>
            <Button
              onClick={() => history.push('/platform')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '20px',
                height: '40px',
                fontWeight: '500'
              }}
            >
              Safety
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ paddingTop: '40px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          {/* Hero Section */}
          <div style={{ 
            marginBottom: '60px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            borderRadius: '20px',
            padding: '60px 40px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📚</div>
            <Title level={1} style={{ 
              fontSize: '3rem', 
              marginBottom: '20px', 
              color: '#2c3e50',
              fontWeight: '700'
            }}>
              Parenting & Child Wellness Blog
            </Title>
            <Paragraph style={{ 
              fontSize: '20px', 
              color: '#7f8c8d', 
              marginBottom: '0',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Expert insights, practical tips, and research-backed strategies to support your child's emotional growth and development.
            </Paragraph>
          </div>

          {/* Search and Filter Section */}
          <div style={{ marginBottom: '40px' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={12}>
                <Search
                  placeholder="Search articles..."
                  prefix={<SearchOutlined />}
                  size="large"
                  onSearch={handleSearch}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} md={8}>
                <Select
                  placeholder="Filter by category"
                  size="large"
                  style={{ width: '100%' }}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  <Option value="all">All Categories</Option>
                  {categories.map(category => (
                    <Option key={category} value={category}>{category}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={4} style={{ textAlign: 'right' }}>
                <Text type="secondary">{filteredPosts.length} articles</Text>
              </Col>
            </Row>
          </div>

          {/* Featured Posts */}
          {selectedCategory === 'all' && !searchQuery && (
            <div style={{ marginBottom: '60px' }}>
              <Title level={2} style={{ marginBottom: '30px', color: '#2c3e50' }}>
                Featured Articles
              </Title>
              <Row gutter={[24, 24]}>
                {blogPosts.filter(post => post.featured).map(post => (
                  <Col xs={24} lg={12} key={post.id}>
                    <Card
                      hoverable
                      style={{ height: '100%' }}
                      cover={
                        <div style={{ 
                          height: '200px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '64px'
                        }}>
                          📖
                        </div>
                      }
                      actions={[
                        <Button 
                          type="primary" 
                          style={{ background: '#667eea', borderColor: '#667eea' }}
                          onClick={() => history.push(`/blog/${post.slug}`)}
                        >
                          Read Article
                        </Button>
                      ]}
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <Tag color="blue">{post.category}</Tag>
                        <Text type="secondary"> • {post.readTime}</Text>
                      </div>
                      <Title level={4} style={{ marginBottom: '12px' }}>
                        {post.title}
                      </Title>
                      <Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: '12px' }}>
                        {post.excerpt}
                      </Paragraph>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary">By {post.author}</Text>
                        <Text type="secondary">{formatDate(post.publishedDate)}</Text>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* All Posts */}
          <div>
            <Title level={2} style={{ marginBottom: '30px', color: '#2c3e50' }}>
              {selectedCategory === 'all' && !searchQuery ? 'All Articles' : 
               searchQuery ? `Search Results for "${searchQuery}"` :
               `${selectedCategory} Articles`}
            </Title>
            
            {filteredPosts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }}>🔍</div>
                <Title level={3} style={{ color: '#7f8c8d' }}>No articles found</Title>
                <Text type="secondary">Try adjusting your search terms or category filter.</Text>
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {filteredPosts.map(post => (
                  <Col xs={24} md={12} lg={8} key={post.id}>
                    <Card
                      hoverable
                      style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                      cover={
                        <div style={{ 
                          height: '160px', 
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={post.image} 
                            alt={post.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentNode.innerHTML = '<div style="height: 160px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); display: flex; align-items: center; justify-content: center; font-size: 48px;">' + (post.featured ? '⭐' : '📄') + '</div>';
                            }}
                          />
                        </div>
                      }
                      bodyStyle={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <Tag color="blue">{post.category}</Tag>
                        <Text type="secondary"> • {post.readTime}</Text>
                      </div>
                      <Title level={5} style={{ marginBottom: '8px', flex: 0 }}>
                        {post.title}
                      </Title>
                      <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: '16px', flex: 1 }}>
                        {post.excerpt}
                      </Paragraph>
                      <div style={{ marginBottom: '16px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>By {post.author}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>{formatDate(post.publishedDate)}</Text>
                      </div>
                      <Button 
                        type="primary" 
                        block
                        icon={<ReadOutlined />}
                        style={{ background: '#667eea', borderColor: '#667eea' }}
                        onClick={() => history.push(`/blog/${post.slug}`)}
                      >
                        Read Article
                      </Button>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>

          {/* Call to Action */}
          <div style={{ 
            marginTop: '80px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            color: 'white'
          }}>
            <Title level={3} style={{ color: 'white', marginBottom: '16px' }}>
              Ready to Support Your Child's Emotional Wellbeing?
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', marginBottom: '24px' }}>
              Discover our wellness companions designed to provide comfort and emotional support.
            </Paragraph>
            <Button 
              type="primary" 
              size="large"
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                fontWeight: 'bold'
              }}
              onClick={() => history.push('/')}
            >
              Explore Lumi Bears
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;